import React, { useState } from 'react';
import { format } from 'date-fns';
import { hrService } from '../../../lib/checkInAttendanceService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LeaveManagement = ({ leaveRequests, loading, onRefresh, staffId, gymId }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    leaveType: 'vacation',
    startDate: '',
    endDate: '',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-success/10 text-success';
      case 'rejected':
        return 'bg-error/10 text-error';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'cancelled':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const leaveTypeOptions = [
    { value: 'vacation', label: 'Vacation', icon: 'Palmtree' },
    { value: 'sick', label: 'Sick Leave', icon: 'Thermometer' },
    { value: 'personal', label: 'Personal', icon: 'User' },
    { value: 'unpaid', label: 'Unpaid', icon: 'DollarSign' },
    { value: 'bereavement', label: 'Bereavement', icon: 'Heart' },
    { value: 'maternity', label: 'Maternity', icon: 'Baby' },
    { value: 'paternity', label: 'Paternity', icon: 'Users' }
  ];

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);

    const totalDays = calculateDays(formData?.startDate, formData?.endDate);

    const { error } = await hrService?.createLeaveRequest({
      gymId,
      staffId,
      leaveType: formData?.leaveType,
      startDate: formData?.startDate,
      endDate: formData?.endDate,
      totalDays,
      reason: formData?.reason
    });

    if (!error) {
      setShowCreateForm(false);
      setFormData({ leaveType: 'vacation', startDate: '', endDate: '', reason: '' });
      onRefresh?.();
    }

    setSubmitting(false);
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-1">Leave Requests</h2>
          <p className="text-sm text-muted-foreground">Manage your time off requests</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={onRefresh} variant="outline" disabled={loading}>
            <Icon name={loading ? 'Loader2' : 'RefreshCw'} size={18} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Button onClick={() => setShowCreateForm(!showCreateForm)}>
            <Icon name={showCreateForm ? 'X' : 'Plus'} size={18} />
            <span>{showCreateForm ? 'Cancel' : 'New Request'}</span>
          </Button>
        </div>
      </div>
      {/* Create Form */}
      {showCreateForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-6 bg-muted/30 rounded-lg border border-border">
          <h3 className="font-semibold text-foreground mb-4">New Leave Request</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Leave Type</label>
              <select
                value={formData?.leaveType}
                onChange={(e) => setFormData({ ...formData, leaveType: e?.target?.value })}
                className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                required
              >
                {leaveTypeOptions?.map((option) => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Start Date</label>
                <Input
                  type="date"
                  value={formData?.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e?.target?.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">End Date</label>
                <Input
                  type="date"
                  value={formData?.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e?.target?.value })}
                  required
                />
              </div>
            </div>

            {formData?.startDate && formData?.endDate && (
              <div className="p-3 bg-primary/10 rounded-lg">
                <p className="text-sm text-foreground">
                  Total Days: <span className="font-semibold">{calculateDays(formData?.startDate, formData?.endDate)}</span>
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Reason</label>
              <textarea
                value={formData?.reason}
                onChange={(e) => setFormData({ ...formData, reason: e?.target?.value })}
                className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                rows={3}
                placeholder="Please provide a reason for your leave request"
                required
              />
            </div>

            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? (
                <Icon name="Loader2" size={18} className="animate-spin" />
              ) : (
                <Icon name="Send" size={18} />
              )}
              <span>Submit Request</span>
            </Button>
          </div>
        </form>
      )}
      {/* Leave Requests List */}
      {loading && leaveRequests?.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : leaveRequests?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Leave Requests</h3>
          <p className="text-muted-foreground">Create your first leave request to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {leaveRequests?.map((request) => (
            <div
              key={request?.id}
              className="p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-foreground capitalize">
                      {request?.leave_type?.replace('_', ' ')} Leave
                    </h3>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(request?.status)}`}>
                      {request?.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(request?.start_date), 'MMM d, yyyy')} -{' '}
                    {format(new Date(request?.end_date), 'MMM d, yyyy')}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">{request?.total_days}</div>
                  <div className="text-xs text-muted-foreground">days</div>
                </div>
              </div>

              {request?.reason && (
                <div className="mb-3 p-3 bg-muted/30 rounded-lg">
                  <p className="text-sm text-foreground">{request?.reason}</p>
                </div>
              )}

              {request?.review_notes && (
                <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <p className="text-xs text-muted-foreground mb-1">Review Notes:</p>
                  <p className="text-sm text-foreground">{request?.review_notes}</p>
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span>Requested: {format(new Date(request?.created_at), 'MMM d, yyyy')}</span>
                {request?.reviewed_at && (
                  <span>Reviewed: {format(new Date(request?.reviewed_at), 'MMM d, yyyy')}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;